import React from 'react'
import type { ComponentProps, PartData } from '@enonic/react-components'
import UpcomingEventsPrototype from '/react4xp/components/prototype/UpcomingEvents'
import type { UpcomingEventsProps } from '/react4xp/components/prototype/UpcomingEvents/types'
import type { EventCardProps } from '/react4xp/components/prototype/EventCard/types'
import { EditorPlaceholder } from '/react4xp/components/shared/EditorPlaceholder'

type ProcessorEventCard = {
	href: string | null
	title: string
	category?: string
	image?: { src: string; alt?: string }
	location?: string
	dateLabel: string
}

type UpcomingEventsPartData = {
	overline?: string
	title?: string
	events?: ProcessorEventCard[]
	viewAllLink?: { label: string; url: string; isExternal: boolean }
	// Emitted by the processor only when `events` is empty (FR-2), to explain the empty
	// state in the Content Studio placeholder below.
	eventsFolderSet?: boolean
	folderResolved?: boolean
	matchedCount?: number
	upcomingCount?: number
}

function toEventCard(card: ProcessorEventCard): EventCardProps | null {
	if (card.href === null) {
		return null
	}

	return {
		image: card.image && { src: card.image.src, alt: card.image.alt || card.title },
		eventType: card.category,
		title: card.title,
		location: card.location,
		date: card.dateLabel,
		url: card.href,
		isExternal: false,
	}
}

function emptyReason(data: UpcomingEventsPartData, eventCount: number, cardCount: number): string {
	// The href-drop case first: the processor returned a non-empty `events`, so it emitted no
	// diagnostic fields (FR-2) and the checks below would all read `undefined`.
	if (eventCount > 0 && cardCount === 0) {
		return `${eventCount} upcoming event(s) found, but none has a page URL.`
	}
	if (!data.eventsFolderSet) {
		return 'No events folder selected.'
	}
	if (!data.folderResolved) {
		return 'The selected events folder could not be found.'
	}
	if ((data.matchedCount ?? 0) > 0 && (data.upcomingCount ?? 0) === 0) {
		return `${data.matchedCount} event(s) in the folder, but none is upcoming.`
	}
	return 'The selected events folder is empty.'
}

export const UpcomingEvents = ({ meta, data }: ComponentProps<PartData>) => {
	const partData = (data || {}) as UpcomingEventsPartData
	const { overline, title, events = [], viewAllLink } = partData
	const eventCards = events.map(toEventCard).filter((card): card is EventCardProps => card !== null)

	// An unconfigured part (or one with zero upcoming events) still has to render
	// something in Content Studio, or the editor cannot select it to fill it in —
	// but it must not render outside edit mode (FR-7). The prototype's own
	// <section><div class="container"> shell has no empty state of its own, so
	// this guard stays even after the swap (FR-15).
	if (eventCards.length === 0) {
		return (
			<EditorPlaceholder
				mode={meta?.mode}
				componentClass="upcoming-events"
				title="Upcoming events"
				heading={[overline, title].filter(Boolean).join(' — ')}
				reason={emptyReason(partData, events.length, eventCards.length)}
			/>
		)
	}

	const props: UpcomingEventsProps = {
		overline,
		title,
		events: eventCards,
		viewAllLink: viewAllLink && {
			text: viewAllLink.label,
			url: viewAllLink.url,
			isExternal: viewAllLink.isExternal,
		},
	}

	return <UpcomingEventsPrototype {...props} />
}
